import React, { useState } from 'react';
import PageHero from './PageHero';
import { ArrowRight } from '../icons';
import {
  Clock,
  MapPin,
  Users,
  School,
  Video,
  CheckCircle2,
  Phone,
  Mail,
  MessageSquare,
  ShieldCheck
} from 'lucide-react';
import heroImg from '../assets/Banner-GMIS Students.png';

export default function ScheduleVisitPage({ setPage, t }) {
  const content = t?.scheduleVisit || {};
  const options = content?.options || {};
  const inPerson = options?.inPerson || {};
  const online = options?.online || {};
  const wte = content?.whatToExpect || {};
  const wteItems = wte?.items || [];
  const formContent = content?.bookingForm || {};
  const formLabels = formContent?.labels || {};
  const cta = content?.cta || {};

  // Form State
  const [visitType, setVisitType] = useState('in-person'); // 'in-person' | 'online'
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    grade: '',
    preferredDate: '',
    preferredTime: '09:00 - 10:30 WIB',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const gradeOptions = [
    'Toddler (Age 2–3)',
    'Nursery (Age 3–4)',
    'Kindergarten (Age 4–5)',
    'Preparatory (Age 5–6)',
    'Primary School (Grades 1–5 / IB PYP)',
    'Middle School (Grades 6–8 / IB MYP)',
    'Senior School (Grades 9–10 / IGCSE)',
    'Senior School (Grades 11–12 / IB DP or CP)'
  ];

  const timeSlots = [
    '08:30 - 10:00 WIB (Morning Session)',
    '10:30 - 12:00 WIB (Mid-day Session)',
    '13:30 - 15:00 WIB (Afternoon Session)'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorMessage) setErrorMessage('');
  };

  const scrollToForm = (type) => {
    if (type) setVisitType(type);
    const formElement = document.getElementById('visit-booking-section');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.grade) {
      setErrorMessage(
        t?.utilAdm === 'Penerimaan'
          ? 'Harap lengkapi semua kolom wajib (Nama, Email, Telepon, dan Jenjang Kelas).'
          : 'Please complete all required fields (Full Name, Email, Phone, and Grade Level).'
      );
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    setTimeout(() => {
      const randomCode = Math.floor(1000 + Math.random() * 9000);
      setReferenceId(`GMIS-VISIT-${randomCode}`);
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 700);
  };

  const handleResetForm = () => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      grade: '',
      preferredDate: '',
      preferredTime: '09:00 - 10:30 WIB',
      message: ''
    });
    setIsSubmitted(false);
  };

  const handleNavigateToAdmissions = () => {
    if (setPage) setPage('admissions-process');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="w-full bg-paper min-h-screen text-ink pb-20">
      
      {/* ── Page Hero ──────────────────────────────────────────────────────── */}
      <PageHero
        eyebrow={content.eyebrow || 'SCHEDULE A VISIT'}
        title={content.title || 'Come and Experience GMIS Jakarta'}
        subtitle={
          content.subtitle ||
          'Choosing a school is an important decision, and visiting the school is one of the best ways to experience what life at GMIS Jakarta is like.'
        }
        bgImage={heroImg}
        breadcrumbs={content.breadcrumbs || ['Home', 'Admissions', 'Schedule a Visit']}
      />

      {/* ── Floating Quick Information Bar ─────────────────────────────────── */}
      <div className="max-w-[1200px] mx-auto px-5 -mt-10 relative z-20">
        <div className="bg-white rounded-[8px] shadow-lg border border-line p-5 lg:p-6 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 divide-y md:divide-y-0 md:divide-x divide-line/70">
          <div className="flex items-center gap-3.5 pt-2 md:pt-0">
            <div className="w-10 h-10 rounded-[4px] bg-paper text-navy flex items-center justify-center shrink-0 border border-line">
              <MapPin className="w-5 h-5 text-gold" />
            </div>
            <div>
              <div className="text-[11px] font-bold tracking-widest text-gold-ink uppercase">Location</div>
              <div className="text-[13px] font-heading font-semibold text-navy">Kemayoran, Central Jakarta</div>
            </div>
          </div>

          <div className="flex items-center gap-3.5 pt-2 md:pt-0 md:pl-6">
            <div className="w-10 h-10 rounded-[4px] bg-paper text-navy flex items-center justify-center shrink-0 border border-line">
              <Clock className="w-5 h-5 text-gold" />
            </div>
            <div>
              <div className="text-[11px] font-bold tracking-widest text-gold-ink uppercase">Campus Hours</div>
              <div className="text-[13px] font-heading font-semibold text-navy">Mon – Fri: 08:00 – 15:30</div>
            </div>
          </div>

          <div className="flex items-center gap-3.5 pt-2 md:pt-0 md:pl-6">
            <div className="w-10 h-10 rounded-[4px] bg-paper text-navy flex items-center justify-center shrink-0 border border-line">
              <School className="w-5 h-5 text-gold" />
            </div>
            <div>
              <div className="text-[11px] font-bold tracking-widest text-gold-ink uppercase">Visit Formats</div>
              <div className="text-[13px] font-heading font-semibold text-navy">On Campus & Virtual</div>
            </div>
          </div>

          <div className="flex items-center gap-3.5 pt-2 md:pt-0 md:pl-6">
            <div className="w-10 h-10 rounded-[4px] bg-paper text-navy flex items-center justify-center shrink-0 border border-line">
              <Users className="w-5 h-5 text-gold" />
            </div>
            <div>
              <div className="text-[11px] font-bold tracking-widest text-gold-ink uppercase">Counseling</div>
              <div className="text-[13px] font-heading font-semibold text-navy">1-on-1 Personalized Tour</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-5 pt-16 lg:pt-20">
        
        {/* ── Section 1: Introduction & Welcome ────────────────────────────── */}
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <div className="text-[12px] font-bold tracking-[0.2em] uppercase text-gold mb-3">
            {content.introBadge || 'CAMPUS EXPERIENCE'}
          </div>

          <h2 className="m-0 font-heading font-medium text-[36px] md:text-[44px] text-navy leading-[1.12] mb-5">
            {content.introTitle || 'Discover GMIS in Action'}
          </h2>

          <p className="m-0 text-[16px] md:text-[18px] text-ink/80 leading-[1.75]">
            {content.introDesc ||
              'Meet our Admissions Team, explore our learning environment and discover our programmes, facilities and community. We’ll be happy to answer your questions and help you understand how GMIS can support your child’s educational journey.'}
          </p>
        </div>

        {/* ── Section 2: Choose Your Visit Experience (Dual Cards) ─────────── */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <div className="text-[12px] font-bold tracking-[0.2em] uppercase text-gold mb-2">
              {options.eyebrow || 'WAYS TO CONNECT'}
            </div>
            <h3 className="m-0 font-heading font-medium text-[32px] md:text-[36px] text-navy">
              {options.title || 'Choose Your Visit Experience'}
            </h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            
            {/* Format A: In-Person Campus Tour */}
            <div className="bg-white rounded-[8px] p-8 lg:p-10 border border-line shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between border-t-[3px] border-t-navy">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 rounded-[4px] bg-paper text-navy flex items-center justify-center border border-line">
                    <School className="w-6 h-6 text-navy" />
                  </div>
                  <span className="text-[11px] font-bold tracking-[0.16em] uppercase text-navy border-b border-navy/30 pb-0.5">
                    {inPerson.badge || 'On Campus'}
                  </span>
                </div>

                <h4 className="font-heading font-semibold text-[24px] text-navy mb-4 leading-snug">
                  {inPerson.title || 'Experience GMIS During School Hours'}
                </h4>

                <p className="text-[15px] leading-[1.75] text-ink/80 mb-6">
                  {inPerson.desc ||
                    'We encourage families to visit during school hours, when possible, to experience the real life of GMIS Jakarta. See our school community in action, observe the learning environment and get a feel for everyday life at GMIS.'}
                </p>

                <div className="space-y-3 mb-8 pt-4 border-t border-line">
                  {(inPerson.highlights || [
                    'Observe live classes and teacher-student interactions',
                    'Tour our smart classrooms, science labs and sports facilities',
                    'Experience our welcoming international community atmosphere'
                  ]).map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-paper text-navy flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-gold" />
                      </div>
                      <span className="text-[14px] text-ink/90 leading-normal">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => scrollToForm('in-person')}
                className="btn w-full h-[48px] flex items-center justify-center gap-2.5 rounded-[3px] bg-navy text-white text-[12.5px] font-bold tracking-widest uppercase hover:bg-gold hover:text-navy-deep transition-all duration-200 cursor-pointer"
              >
                <span>{t?.utilAdm === 'Penerimaan' ? 'Jadwalkan Tur Kampus' : 'Schedule Campus Tour'}</span>
                <ArrowRight size={16} />
              </button>
            </div>

            {/* Format B: Online Consultation */}
            <div className="bg-white rounded-[8px] p-8 lg:p-10 border border-line shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between border-t-[3px] border-t-gold">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 rounded-[4px] bg-paper text-navy flex items-center justify-center border border-line">
                    <Video className="w-6 h-6 text-navy" />
                  </div>
                  <span className="text-[11px] font-bold tracking-[0.16em] uppercase text-gold-ink border-b border-gold/40 pb-0.5">
                    {online.badge || 'Virtual Consultation'}
                  </span>
                </div>

                <h4 className="font-heading font-semibold text-[24px] text-navy mb-4 leading-snug">
                  {online.title || 'Online Consultation'}
                </h4>

                <p className="text-[15px] leading-[1.75] text-ink/80 mb-6">
                  {online.desc ||
                    'For families who are currently overseas or unable to visit the school in person, our Admissions Team is also available for online consultations. Connect with us virtually to learn more about GMIS Jakarta, discuss your child’s needs and ask any questions about the admissions process.'}
                </p>

                <div className="space-y-3 mb-8 pt-4 border-t border-line">
                  {(online.highlights || [
                    'Tailored one-on-one video call with admissions counselors',
                    'Convenient scheduling across international time zones',
                    'Detailed guidance on curricula, documents, and relocation'
                  ]).map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-paper text-navy flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-gold" />
                      </div>
                      <span className="text-[14px] text-ink/90 leading-normal">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => scrollToForm('online')}
                className="btn w-full h-[48px] flex items-center justify-center gap-2.5 rounded-[3px] border-[1.5px] border-navy text-navy text-[12.5px] font-bold tracking-widest uppercase hover:bg-navy hover:text-white transition-all duration-200 cursor-pointer"
              >
                <span>{t?.utilAdm === 'Penerimaan' ? 'Pesan Konsultasi Online' : 'Book Online Consultation'}</span>
                <ArrowRight size={16} />
              </button>
            </div>

          </div>
        </div>

        {/* ── Section 3: What to Expect ───────────────────────────────────── */}
        <div className="mb-20">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <div className="text-[12px] font-bold tracking-[0.2em] uppercase text-gold mb-2">
              {wte.eyebrow || 'TOUR OVERVIEW'}
            </div>
            <h3 className="m-0 font-heading font-medium text-[32px] md:text-[36px] text-navy mb-3">
              {wte.title || 'What to Expect'}
            </h3>
            <p className="m-0 text-[16px] text-ink/80 leading-[1.75]">
              {wte.subtitle ||
                'A structured, personalized visit designed around your family’s questions and educational priorities.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {(wteItems.length > 0 ? wteItems : [
              {
                step: '01',
                title: 'Meet Our Admissions Team',
                desc: 'Discuss your child’s needs, our programmes and the admissions process.'
              },
              {
                step: '02',
                title: 'Explore the School',
                desc: 'Take a guided tour and see our learning spaces and school facilities.'
              },
              {
                step: '03',
                title: 'Discover GMIS',
                desc: 'Learn more about our curriculum, student life and opportunities beyond the classroom.'
              }
            ]).map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-[8px] p-8 border border-line shadow-sm border-t-[3px] border-t-gold flex flex-col justify-between"
              >
                <div>
                  <span className="font-heading font-medium text-[32px] text-navy tracking-tight block mb-4">
                    {item.step || `0${idx + 1}`}
                  </span>

                  <h4 className="font-heading font-semibold text-[20px] text-navy mb-3">
                    {item.title}
                  </h4>
                  
                  <p className="text-[15px] text-ink/80 leading-[1.7] mb-6">
                    {item.desc}
                  </p>
                </div>

                <div className="pt-4 border-t border-line flex items-center gap-2 text-[11.5px] font-bold uppercase tracking-wider text-gold-ink">
                  <ShieldCheck className="w-4 h-4 text-gold shrink-0" />
                  <span>
                    {idx === 0 && (t?.utilAdm === 'Penerimaan' ? 'Konseling Personal' : 'Personalized Counseling')}
                    {idx === 1 && (t?.utilAdm === 'Penerimaan' ? 'Tur Berpemandu Lengkap' : 'Guided Campus Tour')}
                    {idx === 2 && (t?.utilAdm === 'Penerimaan' ? 'Wawasan Kurikulum & Karakter' : 'Curriculum & Student Life')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Interactive Schedule a Visit Form ────────────────── */}
        <div id="visit-booking-section" className="mb-20 scroll-mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Form Column */}
            <div className="lg:col-span-8 bg-white rounded-[8px] p-8 lg:p-12 border border-line shadow-sm">
              
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-line">
                <div>
                  <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-gold mb-1">
                    {formContent.badge || 'ONLINE APPOINTMENT'}
                  </div>
                  <h3 className="m-0 font-heading font-medium text-[28px] md:text-[34px] text-navy">
                    {formContent.title || 'Schedule a School Visit'}
                  </h3>
                  <p className="m-0 text-[14px] text-ink/70 mt-1">
                    {formContent.subtitle ||
                      'Our Admissions Team will contact you to arrange a suitable date and time.'}
                  </p>
                </div>

                {/* Visit Type Segmented Tab Buttons */}
                <div className="flex border border-line rounded-[3px] p-1 bg-paper gap-1 shrink-0 self-start sm:self-center">
                  <button
                    type="button"
                    onClick={() => setVisitType('in-person')}
                    className={`px-4 py-2.5 rounded-[2px] text-[11.5px] font-bold tracking-wider uppercase transition-all duration-200 cursor-pointer ${
                      visitType === 'in-person'
                        ? 'bg-navy text-white shadow-sm'
                        : 'bg-transparent text-navy/70 hover:text-navy hover:bg-white/60'
                    }`}
                  >
                    {formLabels.inPersonOption || 'In-Person Campus Tour'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setVisitType('online')}
                    className={`px-4 py-2.5 rounded-[2px] text-[11.5px] font-bold tracking-wider uppercase transition-all duration-200 cursor-pointer ${
                      visitType === 'online'
                        ? 'bg-navy text-white shadow-sm'
                        : 'bg-transparent text-navy/70 hover:text-navy hover:bg-white/60'
                    }`}
                  >
                    {formLabels.onlineOption || 'Online Consultation'}
                  </button>
                </div>
              </div>

              {isSubmitted ? (
                /* Success State */
                <div className="py-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-paper text-navy flex items-center justify-center mx-auto mb-4 border-2 border-navy">
                    <CheckCircle2 className="w-8 h-8 text-gold" />
                  </div>
                  <h4 className="font-heading font-semibold text-[24px] text-navy mb-2">
                    {formLabels.successTitle || 'Visit Request Received!'}
                  </h4>
                  <p className="text-[15px] text-ink/80 max-w-md mx-auto mb-6 leading-relaxed">
                    {formLabels.successDesc ||
                      'Thank you! Our Admissions Team will contact you shortly to confirm your scheduled date and time.'}
                  </p>

                  <div className="bg-paper rounded-[4px] p-6 max-w-md mx-auto border border-line mb-8 text-left text-sm space-y-2.5">
                    <div className="flex justify-between border-b border-line pb-2">
                      <span className="text-ink/60">Reference Code:</span>
                      <span className="font-heading font-bold text-navy">{referenceId}</span>
                    </div>
                    <div className="flex justify-between border-b border-line pb-2">
                      <span className="text-ink/60">Visit Format:</span>
                      <span className="font-semibold text-navy">
                        {visitType === 'in-person' ? 'In-Person Campus Tour' : 'Online Consultation'}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-line pb-2">
                      <span className="text-ink/60">Parent Name:</span>
                      <span className="font-semibold text-navy">{formData.fullName}</span>
                    </div>
                    <div className="flex justify-between border-b border-line pb-2">
                      <span className="text-ink/60">Grade Level:</span>
                      <span className="font-semibold text-navy">{formData.grade}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink/60">Preferred Session:</span>
                      <span className="font-semibold text-navy">{formData.preferredTime}</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <button
                      onClick={handleResetForm}
                      className="btn h-[48px] px-7 rounded-[3px] border-[1.5px] border-navy text-navy text-[12px] font-bold tracking-widest uppercase hover:bg-navy hover:text-white transition-all cursor-pointer"
                    >
                      {formLabels.scheduleAnother || 'Schedule Another Visit'}
                    </button>
                    <a
                      href="https://wa.me/6281111801448"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn h-[48px] px-7 rounded-[3px] bg-navy text-white text-[12px] font-bold tracking-widest uppercase hover:bg-gold hover:text-navy-deep transition-all inline-flex items-center gap-2 cursor-pointer"
                    >
                      <span>Direct WhatsApp</span>
                      <ArrowRight size={16} />
                    </a>
                  </div>
                </div>
              ) : (
                /* Form Inputs */
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  {errorMessage && (
                    <div className="p-4 rounded-[4px] bg-red-50 border border-red-200 text-red-700 text-sm">
                      {errorMessage}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Parent Name */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[11.5px] font-bold tracking-[0.1em] uppercase text-navy/70">
                        {formLabels.fullName || 'Parent / Guardian Name'} <span className="text-gold">*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        placeholder="e.g. Budi Santoso / Priya Sharma"
                        className="h-[46px] px-4 bg-paper border-2 border-transparent rounded-[4px] text-[15px] text-ink placeholder:text-muted/50 focus:outline-none focus:border-navy focus:bg-white transition-all duration-200"
                        required
                      />
                    </div>

                    {/* Email */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[11.5px] font-bold tracking-[0.1em] uppercase text-navy/70">
                        {formLabels.email || 'Email Address'} <span className="text-gold">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="e.g. parent@example.com"
                        className="h-[46px] px-4 bg-paper border-2 border-transparent rounded-[4px] text-[15px] text-ink placeholder:text-muted/50 focus:outline-none focus:border-navy focus:bg-white transition-all duration-200"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Phone / WhatsApp */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[11.5px] font-bold tracking-[0.1em] uppercase text-navy/70">
                        {formLabels.phone || 'Phone / WhatsApp Number'} <span className="text-gold">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="e.g. +62 812 3456 7890"
                        className="h-[46px] px-4 bg-paper border-2 border-transparent rounded-[4px] text-[15px] text-ink placeholder:text-muted/50 focus:outline-none focus:border-navy focus:bg-white transition-all duration-200"
                        required
                      />
                    </div>

                    {/* Grade Level */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[11.5px] font-bold tracking-[0.1em] uppercase text-navy/70">
                        {formLabels.grade || 'Grade / Stage of Interest'} <span className="text-gold">*</span>
                      </label>
                      <select
                        name="grade"
                        value={formData.grade}
                        onChange={handleInputChange}
                        className="w-full h-[46px] px-4 bg-paper border-2 border-transparent rounded-[4px] text-[15px] text-ink focus:outline-none focus:border-navy focus:bg-white transition-all duration-200 cursor-pointer"
                        required
                      >
                        <option value="">{formLabels.selectGrade || 'Select Grade Level'}</option>
                        {gradeOptions.map((g, i) => (
                          <option key={i} value={g}>
                            {g}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Preferred Date */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[11.5px] font-bold tracking-[0.1em] uppercase text-navy/70">
                        {formLabels.preferredDate || 'Preferred Date'}
                      </label>
                      <input
                        type="date"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleInputChange}
                        className="h-[46px] px-4 bg-paper border-2 border-transparent rounded-[4px] text-[15px] text-ink focus:outline-none focus:border-navy focus:bg-white transition-all duration-200"
                      />
                    </div>

                    {/* Preferred Time Slot */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[11.5px] font-bold tracking-[0.1em] uppercase text-navy/70">
                        {t?.utilAdm === 'Penerimaan' ? 'Waktu Kunjungan yang Disukai' : 'Preferred Session'}
                      </label>
                      <select
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleInputChange}
                        className="w-full h-[46px] px-4 bg-paper border-2 border-transparent rounded-[4px] text-[15px] text-ink focus:outline-none focus:border-navy focus:bg-white transition-all duration-200 cursor-pointer"
                      >
                        {timeSlots.map((slot, i) => (
                          <option key={i} value={slot}>
                            {slot}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Notes / Message */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11.5px] font-bold tracking-[0.1em] uppercase text-navy/70">
                      {formLabels.message || 'Additional Questions / Notes (Optional)'}
                    </label>
                    <textarea
                      name="message"
                      rows={3}
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder={
                        t?.utilAdm === 'Penerimaan'
                          ? 'Tuliskan pertanyaan spesifik atau catatan mengenai putra-putri Anda...'
                          : 'Any specific questions, curriculum preferences, or notes you would like to discuss...'
                      }
                      className="p-4 bg-paper border-2 border-transparent rounded-[4px] text-[15px] text-ink placeholder:text-muted/50 focus:outline-none focus:border-navy focus:bg-white transition-all duration-200 resize-none leading-[1.6]"
                    />
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-line mt-1" />

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn group w-full h-[52px] flex items-center justify-center gap-3 rounded-[3px] bg-navy text-white text-[12.5px] font-bold tracking-widest uppercase transition-all duration-200 hover:bg-gold hover:text-navy-deep cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span>{formLabels.submitting || 'Scheduling...'}</span>
                    ) : (
                      <>
                        <span>{formLabels.submitButton || 'Schedule a School Visit'}</span>
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>

                  <p className="text-[12px] text-center text-ink/60 m-0">
                    {t?.utilAdm === 'Penerimaan'
                      ? 'Tim Penerimaan kami akan mengonfirmasi ketersediaan jadwal melalui WhatsApp atau Email.'
                      : 'Our Admissions Team will confirm schedule availability via WhatsApp or Email.'}
                  </p>
                </form>
              )}

            </div>

            {/* Information & Assistance Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              
              {/* Direct Admissions Hotline */}
              <div className="bg-navy rounded-[8px] p-8 text-white shadow-md border-t-[3px] border-t-gold">
                <h4 className="font-heading font-medium text-[20px] text-white mb-6 flex items-center gap-2.5">
                  <Phone className="w-5 h-5 text-gold" />
                  <span>Admissions Contact</span>
                </h4>

                <div className="space-y-4 text-[14px] text-white/90">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-gold shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-white">GMIS Jakarta Campus</div>
                      <div className="text-white/70 text-[12.5px] mt-0.5">Jl. HBR Motik D6 No. 1, Kemayoran, Central Jakarta</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-gold shrink-0" />
                    <div>
                      <div className="font-semibold text-white">+62 21 6586 5667</div>
                      <div className="text-white/70 text-[12.5px]">Direct Admissions Desk</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-4 h-4 text-gold shrink-0" />
                    <div>
                      <div className="font-semibold text-white">+62 811-1180-1448</div>
                      <div className="text-white/70 text-[12.5px]">WhatsApp Admissions Hotline</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-gold shrink-0" />
                    <div>
                      <div className="font-semibold text-white">admissions@gmis.sch.id</div>
                      <div className="text-white/70 text-[12.5px]">Official Enquiries</div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-white/20">
                  <a
                    href="https://wa.me/6281111801448"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn w-full h-[48px] flex items-center justify-center gap-2.5 rounded-[3px] bg-gold text-navy-deep text-[12px] font-bold tracking-widest uppercase hover:bg-white hover:text-navy transition-all duration-200 cursor-pointer"
                  >
                    <span>Instant WhatsApp Connect</span>
                    <ArrowRight size={16} />
                  </a>
                </div>
              </div>

              {/* Campus Visit Guidelines */}
              <div className="bg-white rounded-[8px] p-7 border border-line shadow-sm border-t-[3px] border-t-navy">
                <div className="flex items-center gap-3 mb-4">
                  <ShieldCheck className="w-5 h-5 text-gold shrink-0" />
                  <h4 className="font-heading font-medium text-[18px] text-navy m-0">
                    {t?.utilAdm === 'Penerimaan' ? 'Petunjuk Kunjungan' : 'Visitor Guidelines'}
                  </h4>
                </div>

                <ul className="space-y-3 text-[13px] text-ink/80 leading-[1.65] pl-0 list-none m-0">
                  <li className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 shrink-0" />
                    <span>
                      {t?.utilAdm === 'Penerimaan'
                        ? 'Masuk melalui Gerbang 1 (Gate 1). Tunjukkan kartu identitas untuk mendapatkan lencana pengunjung.'
                        : 'Enter via Gate 1. Please present a valid photo ID to receive a campus visitor badge.'}
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 shrink-0" />
                    <span>
                      {t?.utilAdm === 'Penerimaan'
                        ? 'Tersedia tempat parkir khusus bagi keluarga dan orang tua calon siswa di dalam area sekolah.'
                        : 'Complimentary secure on-campus parking is available for visiting families.'}
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 shrink-0" />
                    <span>
                      {t?.utilAdm === 'Penerimaan'
                        ? 'Konselor penerimaan kami akan mendampingi keluarga Anda sepanjang tur ruang kelas dan fasilitas.'
                        : 'An admissions counselor will personally accompany your family throughout the tour.'}
                    </span>
                  </li>
                </ul>
              </div>

            </div>

          </div>
        </div>

      </div>

      {/* ── CTA Section (PreschoolPage style) ─────────────────────────────── */}
      <div className="w-full bg-white mt-12">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-16">
          <div className="relative overflow-hidden bg-navy rounded-3xl p-12 md:p-20 text-center shadow-2xl">
            <div className="relative z-10">
              <h3 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight">
                {cta.title || "Ready to Begin Your Child's Journey?"}
              </h3>
              <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-10">
                {cta.subtitle ||
                  'Schedule a campus visit today and see our world-class educational programmes and facilities for yourself.'}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => scrollToForm()}
                  className="bg-gold text-navy px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white hover:text-navy transition-colors duration-300 cursor-pointer shadow-md"
                >
                  {cta.button || 'Schedule a Visit'}
                </button>
                <button
                  onClick={handleNavigateToAdmissions}
                  className="bg-white/10 text-white px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white/20 transition-colors duration-300 border border-white/20 cursor-pointer"
                >
                  {cta.secondaryButton || 'Explore Admissions'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
